/**
 * Session payment acknowledgement — the transition every configurator flow needs, on every rail.
 *
 * `POST ${sessionApiUrl}/payment-acknowledged { session_id }` is what moves a paid session out of
 * `configuration`. The backend does NOT advance a session on its own when the money lands, so a
 * flow that never makes this call leaves the buyer stranded: the invoice is `paid`, the session is
 * still `configuration`, and nothing downstream (onboarding, deploy) will accept it. Verified live
 * on test1 (2026-07-30) against a genuinely paid Stripe invoice — see
 * [`docs/api.owners-hub.session.md`](../../docs/api.owners-hub.session.md).
 *
 * It lives here, provider-agnostic, because all four flows need it: `v0`/`v3` (Stripe) call it once
 * the card charge clears, and `v0-mx`/`v3-mx` (SPEI) call it when the buyer says the transfer is
 * made. Their `data/commerce-gate.ts` re-exports this module so there is exactly ONE definition of
 * the endpoint and one mapping of its response.
 */
import axios from 'axios';

import store from '@/store';

/** What the backend says should happen once a session's payment is acknowledged. */
export interface PaymentAcknowledgement {
  /**
   * `true` — the session is already on `customization`, so send the owner straight into
   * onboarding. `false` — a login-link email has been sent instead, and the session only
   * advances when that link is opened, so the screen must ask them to check their inbox.
   */
  canContinue: boolean;
  /** Whether the owner still has to verify their email address. */
  emailVerificationRequired: boolean;
}

/**
 * Whether a session that has just been LOADED needs its payment acknowledged — i.e. money has
 * arrived but nobody has told the backend to move the session on.
 *
 * Both halves are required, and each rules out a real failure mode:
 *
 *  - **`invoiceStatus === 'paid'`** — the only evidence of settlement. Anything else (`pending`,
 *    `cancelled`, an unknown/absent status) must not trigger the transition: acknowledging an
 *    unpaid session advances it on nothing, and the buyer would reach onboarding without paying.
 *  - **`sessionStatus === 'configuration'`** — the endpoint's precondition. It is NOT idempotent:
 *    called for a session that has already moved on it fails with `INVALID_SESSION_STATE`
 *    (verified live 2026-07-30). A session already past `configuration` has been acknowledged, so
 *    there is nothing left to do.
 *
 * Kept pure (no store, no window, plain strings) so the rule is unit-testable on its own, the
 * same reasoning as [`./deploy-gate`](./deploy-gate.ts) and [`./session-invoice`](./session-invoice.ts).
 */
export function needsPaymentAcknowledgement(
  sessionStatus: string | null | undefined,
  invoiceStatus: string | null | undefined,
): boolean {
  const invoice = typeof invoiceStatus === 'string' ? invoiceStatus.trim() : '';
  const session = typeof sessionStatus === 'string' ? sessionStatus.trim() : '';
  return invoice === 'paid' && session === 'configuration';
}

/** Owner Bearer header, read from the store (the shared Owners Hub convention is the RAW token,
 *  no `Bearer ` prefix). The route resolves purely from `session_id` and works without it, but the
 *  header is sent for consistency with the rest of the session-invoice endpoints. */
function acknowledgeAuthHeaders(): Record<string, string> {
  const authToken = store.getState().authentication.access_token;
  return authToken ? { Authorization: authToken } : {};
}

/**
 * Whether an error is the backend's "this session has already moved on" rejection —
 * HTTP 500 with `error.code === 'INVALID_SESSION_STATE'` ("Session is not in configuration state").
 *
 * `/payment-acknowledged` is NOT idempotent: the second call for the same session fails with
 * exactly this (verified live 2026-07-30 — the first call returned
 * `{can_continue:true, email_verification_required:false}` and advanced the session to
 * `customization`; the second returned 500 `INVALID_SESSION_STATE`). So a caller that can race
 * with itself (two tabs, a remount, a retry) has to recognise it as BENIGN — the transition it
 * wanted has already happened — rather than surface it as a payment error.
 *
 * It carries no verdict, though: the states outside `configuration` include `payment_failed` and
 * `cancelled` as well as `customization`, so this must never be translated into
 * `canContinue: true`. Callers re-read the session and decide from its real status.
 */
export function isSessionStateRejection(err: unknown): boolean {
  const data = (err as { response?: { data?: { error?: { code?: string } } } })?.response?.data;
  return data?.error?.code === 'INVALID_SESSION_STATE';
}

/**
 * Durable per-session latch for the acknowledgement.
 *
 * The transition must fire at most ONCE per session, and in-memory guards are not enough. When
 * the backend answers `can_continue: false` it has sent a login-link email and left the session
 * on `configuration` — so {@link needsPaymentAcknowledgement} is still true, and a plain reload
 * would acknowledge again and send a SECOND email. Persisting the outcome makes the guard survive
 * the page.
 *
 * It also carries the verdict forward, which fixes a real gap: without it, an owner who reloads
 * the confirmation screen loses `can_continue` and the screen falls back to guessing which
 * variant to show.
 *
 * Same shape as the conversion-tracking latch in `@/utils/analytics` — a namespaced
 * `localStorage` key per subject, written once, and every access wrapped because private-mode
 * Safari throws on `setItem`.
 */
const LATCH_PREFIX = 'ows:pay-ack:';

/**
 * The latch is stored as a WRAPPER around the verdict, so "acknowledged, but we never got an
 * answer" stays distinguishable from "acknowledged, and the answer was no".
 *
 * Flattening the two would be a real UX bug: a failed call latched as
 * `{canContinue: false}` would read back as a genuine verdict and pin the confirmation screen to
 * "check your inbox" for an owner who can actually continue. `verdict: null` instead means "no
 * answer", and the screen falls back to its own signals.
 */
interface AcknowledgeLatch {
  verdict: PaymentAcknowledgement | null;
}

/** Whether this session's payment has already been acknowledged from this browser. */
export function hasAcknowledged(sessionId: string): boolean {
  try {
    return localStorage.getItem(`${LATCH_PREFIX}${sessionId}`) != null;
  } catch {
    return false;
  }
}

/** The stored verdict for a session: `null` when never acknowledged here, or acknowledged with
 *  no answer. Pair with {@link hasAcknowledged} to tell those two apart. */
export function readAcknowledgement(sessionId: string): PaymentAcknowledgement | null {
  try {
    const raw = localStorage.getItem(`${LATCH_PREFIX}${sessionId}`);
    if (!raw) return null;
    const { verdict } = JSON.parse(raw) as Partial<AcknowledgeLatch>;
    if (!verdict) return null;
    return {
      canContinue: verdict.canContinue === true,
      emailVerificationRequired: verdict.emailVerificationRequired === true,
    };
  } catch {
    return null;
  }
}

/**
 * Latch the acknowledgement for a session.
 *
 * Called with the verdict on success, and with `null` when the call failed — the latch must be
 * written EITHER WAY. A failure may still have transitioned the session server-side (or sent the
 * mail), so retrying on the next render is exactly the behaviour to avoid.
 */
export function rememberAcknowledgement(sessionId: string, verdict: PaymentAcknowledgement | null): void {
  try {
    const latch: AcknowledgeLatch = { verdict };
    localStorage.setItem(`${LATCH_PREFIX}${sessionId}`, JSON.stringify(latch));
  } catch {
    /* Storage unavailable (private mode) — the in-memory guards still hold for this page. */
  }
}

/**
 * Acknowledge the session's payment: `POST ${sessionApiUrl}/payment-acknowledged { session_id }`
 * → `{ can_continue, email_verification_required }`.
 *
 * This is a real STATE TRANSITION, not a read. It advances the session to `customization` (or
 * sends a login-link email instead) and it is not idempotent, so call it exactly ONCE per
 * session — on the buyer's action, or once on entry for a session that arrives already paid.
 * Never on render.
 *
 * Two consequences worth knowing before calling:
 *  - `configuration_data` can no longer be saved afterwards (`/session/save` →
 *    `INVALID_SESSION_STATE`), so any pending write must be flushed and AWAITED first.
 *  - the caller should re-read the session (`sessionLoad`) after it resolves, so the store's
 *    `status` reflects the transition instead of a stale `configuration`.
 *
 * Throws on failure — including {@link isSessionStateRejection}, which callers treat as benign.
 */
export async function acknowledgePayment(sessionId: string): Promise<PaymentAcknowledgement> {
  const res = await axios.post(
    `${window.config.sessionApiUrl}/payment-acknowledged`,
    { session_id: sessionId },
    { headers: acknowledgeAuthHeaders() },
  );
  const data = (res.data ?? {}) as { can_continue?: boolean; email_verification_required?: boolean };
  return {
    canContinue: data.can_continue === true,
    emailVerificationRequired: data.email_verification_required === true,
  };
}
